import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useCopyToClipboard } from 'react-use'
import { intl } from 'lang/intl'

import { TtButton, TtIcon } from '@23/ui-react'

import { IconComponent } from '../../../sharedLegacy/components'
import { showNotice } from '../../../Layout/Notices/Notices'

import './StreamingInfoPanelComponent.scss'

const CopyButton = ({ value, copyLabel }) => {
  const [, copyToClipboard] = useCopyToClipboard()

  const handleCopy = useCallback(() => {
    copyToClipboard(value)
    showNotice({
      expirationMillis: 2000,
      isClosable: true,
      message: intl.formatMessage({ defaultMessage: 'Copied to clipboard' }),
    })
  }, [copyToClipboard, value])

  if (!value) return null

  return (
    <TtButton
      circle
      size="small"
      label={copyLabel}
      onClick={handleCopy}
      className="streaming-info-copy-btn"
    >
      <TtIcon name="duplicate" library="one" />
    </TtButton>
  )
}

CopyButton.propTypes = {
  value: PropTypes.string,
  copyLabel: PropTypes.string.isRequired,
}

const CopyCell = ({ value, copyLabel, className }) => (
  <td className={className}>
    <span className="copy-cell-inner">
      {value}
      <CopyButton value={value} copyLabel={copyLabel} />
    </span>
  </td>
)

CopyCell.propTypes = {
  value: PropTypes.string,
  copyLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
}

const StreamingInfoPanelComponent = ({
  quality,
  bitrate,
  server,
  streamName,
  fmleURL,
}) => {
  return (
    <div className="streaming-info-panel-component">
      <table>
        <thead>
          <tr>
            <th>
              <FormattedMessage defaultMessage="Quality" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Bitrate" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Server" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Stream key" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{quality}</td>
            <td>{bitrate}</td>
            <CopyCell
              value={server}
              copyLabel={intl.formatMessage({
                defaultMessage: 'Copy server URL',
              })}
              className="small-text"
            />
            <td className="small-text stream-name">
              <span className="copy-cell-inner">
                {streamName}
                <CopyButton
                  value={streamName}
                  copyLabel={intl.formatMessage({
                    defaultMessage: 'Copy stream key',
                  })}
                />
                <IconComponent
                  name="download"
                  size={[16, 16]}
                  color="FFF"
                  tooltip={intl.formatMessage({
                    defaultMessage: 'Download configuration',
                  })}
                  onClick={() => window.open(fmleURL)}
                />
                <IconComponent
                  name="info"
                  size={[16, 16]}
                  color="FFF"
                  onClick={() =>
                    window.open(
                      'https://www.twentythree.net/help/live/streaming-settings'
                    )
                  }
                  tooltip={intl.formatMessage({
                    defaultMessage: 'Get help',
                  })}
                />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

StreamingInfoPanelComponent.propTypes = {
  quality: PropTypes.string,
  bitrate: PropTypes.string,
  server: PropTypes.string,
  streamName: PropTypes.string,
  fmleURL: PropTypes.string,
}

/* eslint-disable-next-line import-x/no-default-export */
export default StreamingInfoPanelComponent
